import { guideEnhancementFor } from "../content/guide-enhancements";
import type { ContentItem } from "../content/types";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Callout } from "../components/Callout";
import { RichContent } from "../components/RichContent";
import { TableOfContents } from "../components/TableOfContents";
import { formatDate, prepareGuide, readingMinutes } from "../lib/article";

type ArticlePageProps = {
  article: ContentItem;
};

export function ArticlePage({ article }: ArticlePageProps) {
  const enhancement = guideEnhancementFor(article);
  const guide = prepareGuide(article.contentHtml);
  const minutes = readingMinutes(article.contentHtml);

  return (
    <article className="guide">
      <header className="guide__head wrap">
        <Breadcrumbs crumbs={[{ label: "Home", href: "/" }, { label: "Care guides", href: "/articles/" }, { label: article.title }]} />
        <p className="index-label">
          <span className="index-label__number">{article.categories[0] ?? "Aquarium guide"}</span>
          <span>{minutes} min read</span>
        </p>
        <h1 className="display guide__title">{article.title}</h1>
        <p className="guide__summary">{article.summary}</p>
        <p className="guide__meta">
          <span>Published <time dateTime={article.publishedAt}>{formatDate(article.publishedAt)}</time></span>
          <span>Updated <time dateTime={article.modifiedAt}>{formatDate(article.modifiedAt)}</time></span>
          <span>By <a href="/about/">Aquarium Helpline</a></span>
        </p>
      </header>
      <div className="guide__figure-wrap wrap">
        {article.featuredImage ? (
          <img className="guide__figure" src={article.featuredImage} alt={article.featuredImageAlt ?? ""} width={1600} height={900} />
        ) : (
          <div className="guide__figure guide__figure--pattern" aria-hidden="true" />
        )}
      </div>
      <div className="guide__layout wrap">
        <aside className="guide__rail">
          <TableOfContents headings={guide.headings} />
          <div className="rail-note">
            <p className="rail-note__title">A note before you start</p>
            <p>Every aquarium is different. Use this guide to narrow the possibilities, then test before making major changes.</p>
            <a className="text-link" href="/consultation/">Ask about your tank</a>
          </div>
        </aside>
        <div className="guide__body">
          <section className="short-answer" aria-labelledby={`quick-answer-${article.id}`}>
            <p className="index-label"><span className="index-label__number">Short answer</span></p>
            <h2 className="short-answer__title" id={`quick-answer-${article.id}`}>What to know first</h2>
            <p className="short-answer__text">{enhancement.quickAnswer}</p>
            <p className="short-answer__next">
              <strong>Your tank may not match the typical pattern.</strong>{" "}
              <a href="/consultation/">Request a 15-minute consultation</a> to reduce trial-and-error and leave with a prioritized next step.
            </p>
          </section>
          <RichContent html={guide.html} />
          <section className="faq" aria-labelledby={`faq-${article.id}`}>
            <p className="index-label"><span className="index-label__number">FAQ</span><span>Common follow-up questions</span></p>
            <h2 className="display" id={`faq-${article.id}`}>Frequently asked questions</h2>
            <dl className="faq__list">
              {enhancement.faqs.map((faq) => (
                <div className="faq__item" key={faq.question}>
                  <dt>{faq.question}</dt>
                  <dd>{faq.answer}</dd>
                </div>
              ))}
            </dl>
          </section>
          <Callout compact />
        </div>
      </div>
    </article>
  );
}
